import { UseQueryResult } from 'react-query';

export * from './removeMarkdown';

/**
 * Returns true if any passed query is loading/idle, false if every query has loaded
 */
export const isLoading = (...queries: Pick<UseQueryResult, 'isLoading' | 'isIdle'>[]) =>
  !queries.every(query => !query.isLoading && !query.isIdle);

/**
 * Returns true if any passed query is loading, false if every query has loaded
 */
export const isMutating = (...queries: Pick<UseQueryResult, 'isLoading'>[]) =>
  !queries.every(query => !query.isLoading);

/**
 * Returns true if any passed query has errored, false if every query has not errored
 */
export const isError = (...queries: Pick<UseQueryResult, 'isError'>[]) => !queries.every(query => !query.isError);

export const assertUnreachable = (param: never): never => {
  throw new Error('unreachable');
};

export const ensureUnreachable = (param: never): void => {
  console.log(new Error('unreachable'), param);
};

/**
 * If the provided string exceeds the specified max length, removes characters from the
 * centre of a string (and replaces with a customisable ellipsis) until it is short enough.
 * e.g. superlonglabelthatwontwrapproperly becomes superlon...pproperly
 */
export function truncateStringFromCentre(
  str: string,
  {
    maxLength = 20,
    ellipsis = '...',
  }: {
    /**
     * Length of final string (including ellipsis)
     */
    maxLength?: number;

    /**
     * Replacement text for truncated characters
     */
    ellipsis?: string;
  } = {},
): string {
  const strLength = str.length;
  // If string does not exceed max length, return string
  if (strLength <= maxLength) {
    return str;
  }

  /* Number of characters to remove from intial string
   * i.e. The amount of characters exceeding the max length + the length of the ellipsis
   */
  const numCharsToRemove = strLength - maxLength + ellipsis.length;

  const middleOfString = strLength / 2;
  // Index where ellipsis should start
  const rmStartIndex = middleOfString - numCharsToRemove / 2;
  // Index where ellipsis should end
  const rmEndIndex = middleOfString + numCharsToRemove / 2;

  return `${str.substring(0, rmStartIndex)}${ellipsis}${str.substring(rmEndIndex)}`;
}

/**
 * Truncates a filename (name + .ext) from the centre, leaving the extension intact, eg:
 * superlongfilenamethatwontwrapproperly.pdf becomes...
 * superlon...pproperly.pdf
 */
export function truncateFilenameFromCentre(
  str: string,
  {
    maxLength = 20,
    ellipsis = '...',
  }: {
    /**
     * Length of final string (inc. extension & ellipsis)
     */
    maxLength?: number;

    /**
     * Replacement text for truncated characters
     */
    ellipsis?: string;
  } = {},
): string {
  const splitChar = '.';

  if (str.includes(splitChar)) {
    const [ext, ...filenameArr] = str.split(splitChar).reverse();
    const filename = filenameArr.reverse().join(splitChar);
    const suffixLength = splitChar.length + ext.length;
    return `${truncateStringFromCentre(filename, { maxLength: maxLength - suffixLength, ellipsis })}${splitChar}${ext}`;
  }

  return truncateStringFromCentre(str, { maxLength, ellipsis });
}

/**
 * Creates a link element to test if provided is external (ie, on a different host to the current page)
 */
export const isExternalUrl = (url: string) => {
  const tmp = document.createElement('a');
  tmp.href = url;
  return tmp.host !== window.location.host;
};

/**
 * Whether a URL is safe to use as a link: http(s), or relative to the current page.
 * Stored URLs can carry other schemes (javascript:, data:), which run script when clicked.
 */
export const isHttpUrl = (url: string): boolean => {
  try {
    return ['http:', 'https:'].includes(new URL(url, 'https://example.invalid').protocol);
  } catch {
    return false;
  }
};
